import React, { useMemo, useRef, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faPlus,
  faCheck,
  faChevronDown,
  faChevronRight,
  faPenToSquare,
  faTrash,
} from '@fortawesome/free-solid-svg-icons'
import type {
  CompetencyArea,
  Competency,
} from '../../types/GrowthWheelManagement'
import {
  useCreateEditorCompetencyMutation,
  useDeleteEditorCompetencyAreaMutation,
  useReorderEditorCompetenciesOnWheelMutation,
  useUpdateEditorCompetencyAreaMutation,
} from '../../redux/growth-wheel.endpoints'
import { trimDesc } from './wheelManagementUtils'
import { WheelManagementDragHandle } from './WheelManagementDragHandle'
import { SubCompetencyRow } from './SubCompetencyRow'
import { WheelManagementDeleteModal } from './WheelManagementDeleteModal'
import {
  getGwWmActiveDrag,
  getGwWmDragData,
  setGwWmActiveDrag,
  setGwWmDragData,
  setGwWmDragPreviewFromNode,
} from './wheelManagementDnD'

type Props = {
  growthWheelId: number
  area: CompetencyArea
  index: number
  allAreas: CompetencyArea[]
  allCompetencies: Competency[]
  onEditCompetency: (c: Competency) => void
  areaDropTargetIndex: number | null
  onAreaDropTargetChange: (index: number | null) => void
}

export function WheelManagementAreaCard({
  growthWheelId,
  area,
  index,
  allAreas,
  allCompetencies,
  onEditCompetency,
  areaDropTargetIndex,
  onAreaDropTargetChange,
}: Props) {
  const cardElRef = useRef<HTMLDivElement>(null)
  const [isExpanded, setIsExpanded] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [editingAreaName, setEditingAreaName] = useState('')
  const [editingAreaDesc, setEditingAreaDesc] = useState('')
  const [editingAreaBaseline, setEditingAreaBaseline] = useState<{
    name: string
    description: string
  } | null>(null)

  const [addingSub, setAddingSub] = useState(false)
  const [newSubName, setNewSubName] = useState('')
  const [newSubDesc, setNewSubDesc] = useState('')

  const [deleteAreaModalOpen, setDeleteAreaModalOpen] = useState(false)

  const [isDragging, setIsDragging] = useState(false)
  const [subDropTargetIndex, setSubDropTargetIndex] = useState<number | null>(
    null,
  )

  const [updateArea] = useUpdateEditorCompetencyAreaMutation()
  const [deleteArea] = useDeleteEditorCompetencyAreaMutation()
  const [createCompetency] = useCreateEditorCompetencyMutation()
  const [reorderWheel] = useReorderEditorCompetenciesOnWheelMutation()

  const areaCompetencies = useMemo(
    () => allCompetencies.filter(c => c.area === area.id),
    [allCompetencies, area.id],
  )

  const areaDragEnabled = !isEditing && !isExpanded
  const isDropTarget = areaDropTargetIndex === index && !isDragging

  const areaEditSaveEnabled =
    isEditing &&
    editingAreaBaseline !== null &&
    Boolean(editingAreaName.trim()) &&
    (editingAreaName !== editingAreaBaseline.name ||
      editingAreaDesc !== editingAreaBaseline.description)

  const startEditArea = () => {
    setIsEditing(true)
    setEditingAreaName(area.name)
    setEditingAreaDesc(area.description)
    setEditingAreaBaseline({
      name: area.name,
      description: area.description,
    })
  }

  const cancelAreaEdit = () => {
    setIsEditing(false)
    setEditingAreaName('')
    setEditingAreaDesc('')
    setEditingAreaBaseline(null)
  }

  const saveAreaEdit = async () => {
    if (
      !editingAreaBaseline ||
      !editingAreaName.trim() ||
      (editingAreaName === editingAreaBaseline.name &&
        editingAreaDesc === editingAreaBaseline.description)
    ) {
      return
    }
    await updateArea({
      growthWheelId,
      areaId: Number(area.id),
      body: {
        name: editingAreaName,
        description: editingAreaDesc,
      },
    }).unwrap()
    setIsEditing(false)
    setEditingAreaName('')
    setEditingAreaDesc('')
    setEditingAreaBaseline(null)
  }

  const persistAreaOrder = async (fromIndex: number, toIndex: number) => {
    const next = [...allAreas]
    const [item] = next.splice(fromIndex, 1)
    next.splice(toIndex, 0, item)
    await reorderWheel({
      growthWheelId,
      orderedCompetencyIds: next.map(a => Number(a.id)),
    }).unwrap()
  }

  const submitNewSub = async () => {
    if (!newSubName.trim()) return
    await createCompetency({
      growthWheelId,
      areaId: Number(area.id),
      body: {
        name: newSubName.trim(),
        description: newSubDesc.trim(),
      },
    }).unwrap()
    setNewSubName('')
    setNewSubDesc('')
    setAddingSub(false)
  }

  const confirmDeleteArea = async () => {
    await deleteArea({
      growthWheelId,
      areaId: Number(area.id),
    }).unwrap()
    setDeleteAreaModalOpen(false)
  }

  const handleAreaDragStart = (e: React.DragEvent<HTMLSpanElement>) => {
    if (!areaDragEnabled) return
    setGwWmDragData(e.dataTransfer, { kind: 'area', fromIndex: index })
    setGwWmDragPreviewFromNode(
      e.dataTransfer,
      e.clientX,
      e.clientY,
      cardElRef.current,
    )
    setGwWmActiveDrag({ kind: 'area', fromIndex: index })
    setIsDragging(true)
  }

  const handleAreaDragEnd = () => {
    setGwWmActiveDrag(null)
    onAreaDropTargetChange(null)
    setIsDragging(false)
  }

  const handleAreaDragOverCapture = (e: React.DragEvent) => {
    const active = getGwWmActiveDrag()
    if (active?.kind !== 'area') return
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    if (active.fromIndex !== index) {
      onAreaDropTargetChange(index)
    }
  }

  const handleAreaDrop = (e: React.DragEvent) => {
    const p = getGwWmDragData(e)
    if (!p || p.kind !== 'area') return
    e.preventDefault()
    onAreaDropTargetChange(null)
    if (p.fromIndex !== index) {
      void persistAreaOrder(p.fromIndex, index)
    }
  }

  const handleExpandBodyDragOver = (e: React.DragEvent) => {
    if (getGwWmActiveDrag()?.kind === 'area') {
      e.preventDefault()
      e.dataTransfer.dropEffect = 'move'
    }
  }

  return (
    <>
      <div
        ref={cardElRef}
        className={`gw-wm__area-card${isDragging ? ' gw-wm__area-card--dragging' : ''}${isDropTarget ? ' gw-wm__area-card--drop-target' : ''}`}
        aria-dropeffect={isDropTarget ? 'move' : undefined}
        onDragOverCapture={handleAreaDragOverCapture}
        onDrop={handleAreaDrop}
      >
        <div className="gw-wm__area-header">
          {isEditing ? (
            <div className="gw-wm__form-stack">
              <div>
                <label className="gw-wm__label">Competency name</label>
                <input
                  type="text"
                  className="gw-wm__input"
                  value={editingAreaName}
                  onChange={e => setEditingAreaName(e.target.value)}
                />
              </div>
              <div>
                <label className="gw-wm__label">Description</label>
                <textarea
                  className="gw-wm__textarea"
                  rows={2}
                  value={editingAreaDesc}
                  onChange={e => setEditingAreaDesc(e.target.value)}
                />
              </div>
              <div className="gw-wm__btn-row">
                <button
                  type="button"
                  className="gw-wm__btn-primary"
                  draggable={false}
                  disabled={!areaEditSaveEnabled}
                  onClick={() => void saveAreaEdit()}
                >
                  <FontAwesomeIcon icon={faCheck} />
                  Save
                </button>
                <button
                  type="button"
                  className="gw-wm__btn-secondary"
                  draggable={false}
                  onClick={cancelAreaEdit}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="gw-wm__area-header-row">
              <div className="gw-wm__area-header-main">
                <WheelManagementDragHandle
                  enabled={areaDragEnabled}
                  onDragStart={handleAreaDragStart}
                  onDragEnd={handleAreaDragEnd}
                  title={
                    areaDragEnabled
                      ? 'Drag to reorder competency'
                      : 'Collapse this competency to reorder it among others'
                  }
                  className="gw-wm__drag-handle--area"
                />
                <button
                  type="button"
                  className="gw-wm__btn-icon"
                  draggable={false}
                  onClick={() => setIsExpanded(v => !v)}
                  aria-label={isExpanded ? 'Collapse' : 'Expand'}
                >
                  <FontAwesomeIcon
                    icon={isExpanded ? faChevronDown : faChevronRight}
                  />
                </button>
                <div className="gw-wm__area-header-text">
                  <h3 className="gw-wm__area-title">{area.name}</h3>
                  <p
                    className={
                      trimDesc(area.description)
                        ? 'gw-wm__area-desc'
                        : 'gw-wm__area-desc gw-wm__desc-placeholder'
                    }
                  >
                    {trimDesc(area.description) || 'No description yet.'}
                  </p>
                  <p className="gw-wm__area-meta">
                    {areaCompetencies.length}{' '}
                    {areaCompetencies.length === 1
                      ? 'sub-competency'
                      : 'sub-competencies'}
                  </p>
                </div>
              </div>
              <div className="gw-wm__area-actions">
                <button
                  type="button"
                  className="gw-wm__btn-icon"
                  draggable={false}
                  title="Edit area"
                  onClick={startEditArea}
                >
                  <FontAwesomeIcon icon={faPenToSquare} />
                </button>
                <button
                  type="button"
                  className="gw-wm__btn-icon gw-wm__btn-icon--danger"
                  draggable={false}
                  title="Delete area"
                  onClick={() => setDeleteAreaModalOpen(true)}
                >
                  <FontAwesomeIcon icon={faTrash} />
                </button>
              </div>
            </div>
          )}
        </div>

        {isExpanded && (
          <div
            className="gw-wm__expand-body"
            onDragOver={handleExpandBodyDragOver}
          >
            {areaCompetencies.length === 0 && !addingSub && (
              <div className="gw-wm__sub-empty">
                <p className="gw-wm__hint mb-1">No sub-competencies yet</p>
                <p className="gw-wm__hint-sm mb-3">
                  Add a sub-competency under this competency.
                </p>
              </div>
            )}
            {areaCompetencies.length > 0 && (
              <div
                className="gw-wm__sub-list"
                onDragLeave={e => {
                  if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                    setSubDropTargetIndex(null)
                  }
                }}
              >
                {areaCompetencies.map((competency, compIndex) => (
                  <SubCompetencyRow
                    key={competency.id}
                    growthWheelId={growthWheelId}
                    competency={competency}
                    areaId={area.id}
                    areaIndex={index}
                    compIndex={compIndex}
                    siblingCompetencies={areaCompetencies}
                    subDropTargetIndex={subDropTargetIndex}
                    onSubDropTargetChange={setSubDropTargetIndex}
                    onEdit={() => onEditCompetency(competency)}
                    onAreaReorderDrag={(from, to) =>
                      void persistAreaOrder(from, to)
                    }
                  />
                ))}
              </div>
            )}

            {addingSub ? (
              <div
                className="gw-wm__form-panel"
                style={{ marginTop: '0.75rem' }}
              >
                <h4 className="gw-wm__form-panel-title">
                  Add new sub-competency
                </h4>
                <p className="gw-wm__hint-sm mb-2">
                  Add these under a competency after you expand it.
                </p>
                <div className="gw-wm__form-stack">
                  <input
                    type="text"
                    className="gw-wm__input"
                    value={newSubName}
                    onChange={e => setNewSubName(e.target.value)}
                  />
                  <textarea
                    className="gw-wm__textarea"
                    rows={2}
                    value={newSubDesc}
                    onChange={e => setNewSubDesc(e.target.value)}
                    placeholder="Brief description (optional)"
                  />
                  <div className="gw-wm__btn-row">
                    <button
                      type="button"
                      className="gw-wm__btn-primary"
                      draggable={false}
                      disabled={!newSubName.trim()}
                      onClick={() => void submitNewSub()}
                    >
                      <FontAwesomeIcon icon={faCheck} />
                      Add
                    </button>
                    <button
                      type="button"
                      className="gw-wm__btn-secondary"
                      draggable={false}
                      onClick={() => {
                        setAddingSub(false)
                        setNewSubName('')
                        setNewSubDesc('')
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <button
                type="button"
                className="gw-wm__add-dashed"
                draggable={false}
                onClick={() => setAddingSub(true)}
              >
                <FontAwesomeIcon icon={faPlus} />
                Add Sub-Competency
              </button>
            )}
          </div>
        )}
      </div>

      {deleteAreaModalOpen && (
        <WheelManagementDeleteModal
          title="Delete competency"
          confirmLabel="Delete competency"
          onCancel={() => setDeleteAreaModalOpen(false)}
          onConfirm={() => void confirmDeleteArea()}
        >
          <p className="gw-wm__hint">
            Are you sure you want to delete &quot;{area.name}&quot;? This will
            also delete all {areaCompetencies.length} sub-competencies, their
            levels 1–5 rubric definitions, and any associated assessment scores
            and notes.
          </p>
        </WheelManagementDeleteModal>
      )}
    </>
  )
}
